import React, { useState, useRef, useEffect } from 'react';
import './ViewModeDropdown.css';

export interface ViewOption<T extends string = string> {
  value: T;
  label: string;
}

interface ViewModeDropdownProps<T extends string = string> {
  options: ViewOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  label?: string;
}

function ViewModeDropdown<T extends string = string>({
  options,
  value,
  onChange,
  className,
  label = 'View',
}: ViewModeDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Click-outside to close
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectedLabel = options.find(o => o.value === value)?.label ?? value;

  return (
    <div className={`view-mode-dropdown${className ? ` ${className}` : ''}`} ref={containerRef}>
      <span className="view-mode-dropdown__label">{label}</span>
      <button
        type="button"
        className={`view-mode-dropdown__trigger${isOpen ? ' view-mode-dropdown__trigger--open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select view mode"
        onKeyDown={(e) => { if (e.key === 'Escape') setIsOpen(false); }}
      >
        <span className="view-mode-dropdown__selected">{selectedLabel}</span>
        <svg
          className={`view-mode-dropdown__chevron${isOpen ? ' view-mode-dropdown__chevron--open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="view-mode-dropdown__menu" role="listbox">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`view-mode-dropdown__option${value === option.value ? ' view-mode-dropdown__option--selected' : ''}`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              role="option"
              aria-selected={value === option.value}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ViewModeDropdown;
